import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import { ColorModeContext } from "../../theme/ColorModeContext";

export interface DiagnosisSeverityOption {
  label: string;
  value: string;
  color: string;
  bg?: string;
  bordercolor?: string;
  icon?: React.ReactNode;
}

/** Brighter label colors for unselected buttons on dark surfaces */
const darkUnselectedColor: Record<string, string> = {
  SEVERE: "#f1aeb5",
  MODERATE: "#f0ac2f",
  MILD: "#f0d073",
  NORMAL: "#a3cfbb",
  UNCATEGORIZED: "#a8a8a8",
};

interface Props {
  value: string;
  options: DiagnosisSeverityOption[];
  onChange: (value: string) => void;
}

const DiagnosisSeverityToggle: React.FC<Props> = ({
  value,
  options,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<any>({});
  const { mode } = useContext(ColorModeContext);
  const dark = mode === "dark";
  const borderColor = dark ? "#3a3a3e" : "#ddd";

  useEffect(() => {
    const buttons = containerRef.current?.querySelectorAll(".severity-btn");

    const index = options.findIndex((x) => x.value === value);

    if (buttons && buttons[index]) {
      const btn = buttons[index] as HTMLElement;

      setIndicatorStyle({
        width: btn.offsetWidth,
        height: btn.offsetHeight,
        left: btn.offsetLeft,
        top: btn.offsetTop,
        backgroundColor: options[index].bg,
      });
    }
  }, [value, options]);

  return (
    <Box
      ref={containerRef}
      className="diagnosis-severity-toggle"
      sx={{
        position: "relative",
        display: "flex",
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        overflow: "hidden",
        flexWrap: { xs: "wrap", md: "nowrap" },
        backgroundColor: dark ? "#1c1c1e" : "transparent",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          transition: "all .35s ease",
          borderRadius: 0,
          zIndex: 1,
          ...indicatorStyle,
        }}
      />

      {options.map((item) => {
        const selected = value === item.value;
        const labelColor = selected
          ? item.color
          : dark
            ? darkUnselectedColor[item.value] || item.bordercolor || "#f5f5f5"
            : item.color;

        return (
          <ButtonBase
            key={item.value}
            className={`severity-btn${selected ? " severity-btn--selected" : ""}`}
            onClick={() => onChange(item.value)}
            data-severity={item.value}
            sx={{
              flex: {
                xs: "0 0 50%",
                sm: 1,
              },
              maxWidth: {
                xs: "50%",
                sm: "none",
              },
              height: 48,
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              fontWeight: 600,
              color: `${labelColor} !important`,
              WebkitTextFillColor: `${labelColor} !important`,
              transition: "color .3s ease",
              borderRight: {
                md: `1px solid ${borderColor}`,
              },
              borderBottom: {
                xs: `1px solid ${borderColor}`,
                sm: "none",
              },

              "&:nth-of-type(odd)": {
                borderRight: {
                  xs: `1px solid ${borderColor}`,
                  sm: `1px solid ${borderColor}`,
                },
              },

              "&:nth-of-type(even)": {
                borderRight: {
                  xs: "none",
                  sm: `1px solid ${borderColor}`,
                },
              },

              "&:last-of-type": {
                borderRight: "none",
                borderBottom: "none",
              },
            }}
          >
            {item.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default DiagnosisSeverityToggle;
